export type QualityLevel = 1 | 2 | 3 | 4 | 5;

export interface DocumentTypeDefinition {
  id: string;
  code: string;
  title: string;
  description: string;
  frameworks: string[];
  domain: 'TI' | 'Cybersécurité' | 'Ventes & Marketing' | 'Infonuagique' | 'Admin & RH' | 'Administration' | 'RH' | 'Général';
  color: 'blue' | 'indigo' | 'purple' | 'orange' | 'emerald' | 'red';
  status: 'production' | 'coming_soon' | 'archived';
  approved_dany?: number; // 0-5 étoiles (0 = non renseigné, 1-5 = étoiles)
  approved_specialist?: number; // 0-5 étoiles (0 = non renseigné, 1-5 = étoiles)
}

export interface QualityLevelDefinition {
  level: QualityLevel;
  label: string;
  shortLabel: string;
  description: string;
}

export const DOCUMENT_TYPES: DocumentTypeDefinition[] = [
  {
    id: 'prisme-kmi',
    code: 'PRISME_KMI',
    title: 'Prisme - KMI (diagnostic 6 piliers)',
    description:
      'Diagnostic de maturité TI actuelle en 6 piliers (KMI). Génère un résumé exécutif et des recommandations ciblées à partir des scores 0 à 5 par pilier (0 = on ne s\'en occupe pas). Alimente le spider « Maturité actuelle » dans PRIsME Client.',
    frameworks: ['ITIL 4', 'COBIT 2019', 'NIST CSF 2.0'],
    domain: 'TI',
    color: 'emerald',
    status: 'archived',
    approved_dany: 0,
    approved_specialist: 0,
  },
  {
    id: 'prisme-kmi-cible',
    code: 'PRISME_KMI_CIBLE',
    title: 'Prisme - KMI (maturité cible Futur)',
    description:
      'Vision de maturité cible Futur en 6 piliers (KMI), à partir du même questionnaire que le diagnostic actuel. Génère un résumé exécutif orienté cible et alimente le spider « Maturité cible » dans PRIsME Client.',
    frameworks: ['ITIL 4', 'COBIT 2019', 'NIST CSF 2.0'],
    domain: 'TI',
    color: 'indigo',
    status: 'archived',
    approved_dany: 0,
    approved_specialist: 0,
  },
  {
    id: 'prise-de-besoin-client-2026',
    code: 'PRISE_BESOIN_CLIENT_2026',
    title: 'Prise de besoin - Maturité et besoins client (tableau)',
    description:
      'Document tabulaire généré à partir du formulaire combiné : scores KMI As-Was/As-Is/To-Be, barèmes 0 à 5, problèmes/besoins, priorités, objectifs SMART, solutions, coûts, efforts, risques et mitigations.',
    frameworks: [],
    domain: 'TI',
    color: 'emerald',
    status: 'production',
    approved_dany: 0,
    approved_specialist: 0,
  },
  {
    id: 'prise-de-besoin-client-2026-analyse',
    code: 'PRISE_BESOIN_CLIENT_2026_ANALYSE',
    title: 'Prise de besoin - Maturité et besoins client — Analyse & recommandations',
    description:
      'Document narratif professionnel pour un Directeur TI, généré à partir du formulaire « Prise de besoin - Maturité et besoins client » : spiders Passé (As-Was), Présent (As-Is) et Futur (To-Be), synthèse par axe, priorisation, recommandations, bonnes pratiques (génériques) et prochaines étapes. Sans invention de faits; les informations manquantes sont indiquées comme « Non précisé ».',
    frameworks: ['ITIL 4', 'COBIT 2019', 'NIST CSF 2.0'],
    domain: 'TI',
    color: 'emerald',
    status: 'production',
    approved_dany: 0,
    approved_specialist: 0,
  },
  {
    id: 'plan-directeur',
    code: 'PLAN_DIRECTEUR',
    title: 'Plan Directeur TI - Vision Stratégique (3-5 ans)',
    description:
      'Vision stratégique TI à moyen / long terme pour le client, alignée sur les objectifs d\'affaires et les meilleurs cadres (ITIL, COBIT, NIST).',
    frameworks: ['ITIL', 'COBIT', 'NIST CSF'],
    domain: 'TI',
    color: 'indigo',
    status: 'production',
    approved_dany: 5,
    approved_specialist: 0,
  },
  {
    id: 'plan-directeur-5-ans-v2',
    code: 'PLAN_DIRECTEUR_5_ANS_V2',
    title: 'Plan Directeur 5 ans V2',
    description:
      'Plan directeur 5 ans basé sur le cadre référentiel BZ 6 + 2, avec maturité actuelle issue du formulaire Prisme - KMI et contexte stratégique issu de la Prise de besoin - Maturité et besoins client.',
    frameworks: ['ITIL 4', 'COBIT 2019', 'NIST CSF 2.0'],
    domain: 'TI',
    color: 'indigo',
    status: 'production',
    approved_dany: 0,
    approved_specialist: 0,
  },
  {
    id: 'plan-directeur-technique',
    code: 'PLAN_DIRECTEUR_TECHNIQUE',
    title: 'Plan Directeur TI - Analyse Technique & Budget',
    description:
      'Analyse technique approfondie de l\'infrastructure TI avec budget détaillé, stratégies de sauvegardes, RTO/RPO, équipements et recommandations opérationnelles. Document technique orienté infrastructure et investissements.',
    frameworks: [],
    domain: 'TI',
    color: 'blue',
    status: 'production',
    approved_dany: 5,
    approved_specialist: 0,
  },
  {
    id: 'roadmap-24m',
    code: 'ROADMAP_24M',
    title: 'Feuille de route TI (12-24 mois)',
    description:
      'Roadmap TI détaillée sur 12-24 mois, dérivée de la prise de besoin et du Plan Directeur, structurée par piliers TI, trimestres et initiatives.',
    frameworks: ['ITIL', 'COBIT', 'NIST CSF'],
    domain: 'TI',
    color: 'blue',
    status: 'production',
    approved_dany: 5,
    approved_specialist: 0,
  },
  {
    id: 'objectifs-kpi-ti',
    code: 'OBJECTIFS_KPI_TI',
    title: 'Cadre d’objectifs stratégiques TI & KPIs',
    description:
      'Cadre d’objectifs TI alignés aux objectifs d’affaires, avec KPIs par pilier, cibles, fréquences de suivi et responsabilités.',
    frameworks: ['ITIL', 'COBIT'],
    domain: 'TI',
    color: 'indigo',
    status: 'production',
    approved_dany: 5,
    approved_specialist: 0,
  },
  {
    id: 'cahier-charge-ti',
    code: 'CAHIER_CHARGE',
    title: "Spécifications d'exigences d'affaires et TI",
    description:
      "Spécifications d'exigences fonctionnelles et techniques dérivées de la prise de besoin Plan Directeur TI, avec périmètre, exigences d'affaires et TI, critères d'acceptation et contraintes.",
    frameworks: ['ITIL', 'COBIT'],
    domain: 'TI',
    color: 'blue',
    status: 'production',
    approved_dany: 5,
    approved_specialist: 0,
  },
  {
    id: 'matrice-raci-ti',
    code: 'MATRICE_RACI_TI',
    title: 'Matrice de responsabilité TI / client (RACI)',
    description:
      'Matrice RACI détaillée pour la gouvernance TI: rôles BZ, client, DTI, métiers, et responsabilités par piliers, processus et activités clés.',
    frameworks: ['ITIL', 'COBIT'],
    domain: 'TI',
    color: 'emerald',
    status: 'production',
    approved_dany: 5,
    approved_specialist: 0,
  },
  {
    id: 'plan-budgetaire-ti-n1',
    code: 'PLAN_BUDGETAIRE_TI_N1',
    title: 'Plan budgétaire TI annuel (N+1)',
    description:
      'Plan budgétaire TI pour l\'année N+1, structuré par piliers TI, avec répartition CAPEX/OPEX, scénarios et liens avec la Roadmap et les objectifs stratégiques.',
    frameworks: ['ITIL', 'COBIT'],
    domain: 'TI',
    color: 'purple',
    status: 'production',
    approved_dany: 5,
    approved_specialist: 0,
  },
  {
    id: 'plan-budgetaire-5-ans',
    code: 'PLAN_BUDGETAIRE_5_ANS',
    title: 'Plan budgétaire TI – Vision 5 ans',
    description:
      'Plan budgétaire TI sur 5 ans (vision pluriannuelle), généré à partir du formulaire « Prise de besoin - Plan budgétaire 5 ans » : tableau par poste et par année, projets en cours, améliorations.',
    frameworks: ['ITIL', 'COBIT'],
    domain: 'TI',
    color: 'purple',
    status: 'production',
    approved_dany: 5,
    approved_specialist: 0,
  },
  {
    id: 'registre-amelioration-continue-ti',
    code: 'REGISTRE_AMELIORATION_CONTINUE_TI',
    title: 'Registre d\'amélioration continue (backlog TI)',
    description:
      'Registre structuré des améliorations TI à réaliser, organisé par piliers, priorités et effort, avec liens aux objectifs stratégiques et à la Roadmap.',
    frameworks: ['ITIL', 'COBIT'],
    domain: 'TI',
    color: 'orange',
    status: 'production',
    approved_dany: 5,
    approved_specialist: 0,
  },
  {
    id: 'registre-risques-ti',
    code: 'REGISTRE_RISQUES_TI',
    title: 'Registre des risques TI & plan de traitement',
    description:
      'Registre structuré des risques TI identifiés avec analyse d\'impact, probabilité, niveau de risque, et plan de traitement détaillé par pilier.',
    frameworks: ['ITIL', 'COBIT', 'NIST'],
    domain: 'TI',
    color: 'red',
    status: 'production',
    approved_dany: 5,
    approved_specialist: 0,
  },
  {
    id: 'offre-service-bz',
    code: 'OFFRE_SERVICE_BZ',
    title: 'Offre de service BZ - Solution d\'affaire holistique',
    description:
      'Proposition commerciale BZ avec 3 offres adaptées au budget client, basée sur le Cadre BZ (7 piliers, 70 tactiques), avec visualisation AS-IS vs TARGET et livrables tangibles.',
    frameworks: ['ITIL', 'COBIT', 'Cadre BZ'],
    domain: 'Ventes & Marketing',
    color: 'indigo',
    status: 'production',
    approved_dany: 5,
    approved_specialist: 0,
  },
  {
    id: 'ebauche-ods',
    code: 'EBAUCHE_ODS',
    title: 'Ébauche d\'ODS',
    description:
      'Ébauche d’offre de service sans bloc mockup, générée automatiquement à partir des dernières prises de besoin ODS, Client 2026 et Prisme - KMI du client, avec analyse selon les 6 piliers du cadre client BZ.',
    frameworks: ['ITIL', 'COBIT', 'Cadre client BZ', 'PRIsME KMI'],
    domain: 'Ventes & Marketing',
    color: 'indigo',
    status: 'production',
    approved_dany: 0,
    approved_specialist: 0,
  },
  {
    id: 'politique-cyber',
    code: 'POLITIQUE_CYBER',
    title: 'Politique de cybersécurité sur l\'utilisation des actifs TI',
    description:
      'Politique complète de cybersécurité alignée sur NIST CSF 2.0, ITIL 4, COBIT 2019 et ISO 27001. Couvre : utilisations autorisées, logiciels, médias sociaux, BYOD, contrôles d\'accès, authentification, mots de passe, courriel, restrictions et IA.',
    frameworks: ['NIST CSF 2.0', 'ITIL 4', 'COBIT 2019', 'ISO 27001'],
    domain: 'Cybersécurité',
    color: 'purple',
    status: 'production',
    approved_dany: 3,
    approved_specialist: 0,
  },
  {
    id: 'rapport-incident',
    code: 'RAPPORT_INCIDENT',
    title: 'Rapport d\'incident TI',
    description:
      'Rapport d\'incident complet conforme ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0. Documente la chronologie, l\'analyse de cause racine, les impacts, les mesures de confinement/éradication/restauration, et les recommandations préventives.',
    frameworks: ['ITIL 4', 'COBIT 2019', 'ISO 27001', 'NIST CSF 2.0'],
    domain: 'Cybersécurité',
    color: 'red',
    status: 'production',
    approved_dany: 3,
    approved_specialist: 0,
  },
  {
    id: 'politique-ia',
    code: 'POLITIQUE_IA',
    title: 'Politique d\'utilisation de l\'intelligence artificielle',
    description:
      'Politique complète d\'utilisation de l\'IA alignée sur NIST AI RMF, ITIL 4, COBIT 2019 et ISO 27001. Couvre : gouvernance IA, cartographie des risques, mesure et gestion des risques, utilisations autorisées, éthique, conformité, et responsabilités.',
    frameworks: ['NIST AI RMF', 'ITIL 4', 'COBIT 2019', 'ISO 27001'],
    domain: 'Cybersécurité',
    color: 'purple',
    status: 'production',
    approved_dany: 3,
    approved_specialist: 0,
  },
  {
    id: 'politique-gestion-incidents',
    code: 'INCIDENT_RESPONSE_POLICY',
    title: 'Politique de gestion des incidents',
    description:
      'Document maître de gouvernance pour la gestion des incidents TI. Définit les objectifs, la portée, la gouvernance, les enjeux et les responsabilités. Aligné sur ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0.',
    frameworks: ['ITIL 4', 'COBIT 2019', 'ISO 27001', 'NIST CSF 2.0'],
    domain: 'Cybersécurité',
    color: 'red',
    status: 'production',
    approved_dany: 3,
    approved_specialist: 0,
  },
  {
    id: 'politique-securite-information',
    code: 'INFORMATION_SECURITY_POLICY',
    title: 'Politique de sécurité de l\'information',
    description:
      'Politique de référence générale pour la sécurité de l\'information. Document maître que le plan d\'incident et autres politiques doivent respecter. Aligné sur ISO 27001, ITIL 4, COBIT 2019 et NIST CSF 2.0.',
    frameworks: ['ISO 27001', 'ITIL 4', 'COBIT 2019', 'NIST CSF 2.0'],
    domain: 'Cybersécurité',
    color: 'blue',
    status: 'production',
    approved_dany: 3,
    approved_specialist: 0,
  },
  {
    id: 'politique-classification-incidents',
    code: 'INCIDENT_CLASSIFICATION_POLICY',
    title: 'Politique de classification des incidents',
    description:
      'Politique définissant la sévérité, l\'impact, l\'urgence et les niveaux de priorité (P1-P4 ITIL / CAT 1-5 NIST). Alignée sur ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0.',
    frameworks: ['ITIL 4', 'COBIT 2019', 'ISO 27001', 'NIST CSF 2.0'],
    domain: 'Cybersécurité',
    color: 'orange',
    status: 'production',
    approved_dany: 3,
    approved_specialist: 0,
  },
  {
    id: 'politique-escalade-incidents',
    code: 'INCIDENT_ESCALATION_POLICY',
    title: 'Politique d\'escalade des incidents',
    description:
      'Politique définissant les responsables, délais d\'escalade, critères et processus de communication pour l\'escalade des incidents. Alignée sur ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0.',
    frameworks: ['ITIL 4', 'COBIT 2019', 'ISO 27001', 'NIST CSF 2.0'],
    domain: 'Cybersécurité',
    color: 'red',
    status: 'production',
    approved_dany: 3,
    approved_specialist: 0,
  },
  {
    id: 'plan-intervention-incident',
    code: 'INCIDENT_RESPONSE_PLAN',
    title: 'Plan d\'intervention en cas d\'incident (IRP)',
    description:
      'Plan détaillé d\'intervention pour répondre aux incidents de sécurité. Définit les procédures, rôles, outils et workflows pour la détection, analyse, confinement, éradication et restauration. Aligné sur NIST CSF 2.0, ITIL 4, ISO 27001 et COBIT 2019.',
    frameworks: ['NIST CSF 2.0', 'ITIL 4', 'ISO 27001', 'COBIT 2019'],
    domain: 'Cybersécurité',
    color: 'red',
    status: 'production',
    approved_dany: 3,
    approved_specialist: 0,
  },
  {
    id: 'plan-incident-majeur',
    code: 'MAJOR_INCIDENT_PLAN',
    title: 'Plan d\'incident majeur',
    description:
      'Plan spécifique pour la gestion des incidents majeurs (P1/CAT 1). Définit les procédures d\'activation, l\'équipe d\'intervention, les workflows critiques et les processus de communication d\'urgence. Aligné sur ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0.',
    frameworks: ['ITIL 4', 'COBIT 2019', 'ISO 27001', 'NIST CSF 2.0'],
    domain: 'Cybersécurité',
    color: 'red',
    status: 'production',
    approved_dany: 3,
    approved_specialist: 0,
  },
  {
    id: 'plan-communication-incident',
    code: 'INCIDENT_COMMUNICATION_PLAN',
    title: 'Plan de communication d\'incident',
    description:
      'Plan détaillé pour la communication lors d\'incidents. Définit les canaux, templates, audiences, messages et processus de communication interne et externe. Aligné sur NIST CSF 2.0, ITIL 4, ISO 27001 et COBIT 2019.',
    frameworks: ['NIST CSF 2.0', 'ITIL 4', 'ISO 27001', 'COBIT 2019'],
    domain: 'Cybersécurité',
    color: 'orange',
    status: 'production',
    approved_dany: 3,
    approved_specialist: 0,
  },
  {
    id: 'plan-continuite-ti',
    code: 'ITSCM_PLAN',
    title: 'Plan de continuité TI (ITSCM)',
    description:
      'Plan de continuité des services TI pour assurer la reprise après sinistre. Définit les stratégies de sauvegarde, restauration, sites de secours et processus de continuité. Aligné sur ITIL 4, COBIT 2019, ISO 27001 et ISO 22301.',
    frameworks: ['ITIL 4', 'COBIT 2019', 'ISO 27001', 'ISO 22301'],
    domain: 'Cybersécurité',
    color: 'blue',
    status: 'production',
    approved_dany: 3,
    approved_specialist: 0,
  },
  {
    id: 'plan-incident-vie-privee',
    code: 'PRIVACY_INCIDENT_PLAN',
    title: 'Plan d\'incident vie privée / confidentialité',
    description:
      'Plan spécifique pour la gestion des incidents de protection des renseignements personnels. Définit les procédures de détection, évaluation, notification CAI, avis aux personnes concernées et mesures correctives. Aligné sur Loi 25, RGPD, ISO 27001 et ITIL 4.',
    frameworks: ['Loi 25', 'RGPD', 'ISO 27001', 'ITIL 4'],
    domain: 'Cybersécurité',
    color: 'purple',
    status: 'production',
    approved_dany: 3,
    approved_specialist: 0,
  },
  {
    id: 'procedure-gestion-incidents',
    code: 'INCIDENT_MANAGEMENT_PROCEDURE',
    title: 'Procédure de gestion des incidents (ITIL)',
    description:
      'Procédure opérationnelle détaillée pour la gestion des incidents TI selon ITIL 4. Définit les étapes, workflows, rôles et responsabilités pour la détection, enregistrement, classification, résolution et fermeture des incidents.',
    frameworks: ['ITIL 4', 'COBIT 2019', 'ISO 27001'],
    domain: 'Cybersécurité',
    color: 'blue',
    status: 'production',
    approved_dany: 3,
    approved_specialist: 0,
  },
  {
    id: 'procedure-reponse-incidents-securite',
    code: 'SECURITY_INCIDENT_RESPONSE_PROCEDURE',
    title: 'Procédure de réponse aux incidents de sécurité (NIST)',
    description:
      'Procédure opérationnelle détaillée pour la réponse aux incidents de sécurité selon NIST CSF 2.0. Définit les étapes, workflows et actions pour la détection, analyse, confinement, éradication et restauration des incidents de sécurité.',
    frameworks: ['NIST CSF 2.0', 'ITIL 4', 'ISO 27001'],
    domain: 'Cybersécurité',
    color: 'red',
    status: 'production',
    approved_dany: 3,
    approved_specialist: 0,
  },
  {
    id: 'procedure-escalade',
    code: 'ESCALATION_PROCEDURE',
    title: 'Procédure d\'escalade',
    description:
      'Procédure opérationnelle détaillée pour l\'escalade des incidents. Définit les critères, délais, responsables et workflows pour l\'escalade hiérarchique et fonctionnelle des incidents selon ITIL 4.',
    frameworks: ['ITIL 4', 'COBIT 2019', 'ISO 27001'],
    domain: 'Cybersécurité',
    color: 'orange',
    status: 'production',
    approved_dany: 3,
    approved_specialist: 0,
  },
  {
    id: 'procedure-gestion-preuves',
    code: 'EVIDENCE_MANAGEMENT_PROCEDURE',
    title: 'Procédure de gestion des preuves',
    description:
      'Procédure opérationnelle détaillée pour la gestion des preuves lors d\'incidents. Définit les processus de collecte, préservation, analyse et documentation des preuves numériques selon les standards forensiques et ITIL 4.',
    frameworks: ['ITIL 4', 'ISO 27001', 'NIST CSF 2.0'],
    domain: 'Cybersécurité',
    color: 'purple',
    status: 'production',
    approved_dany: 3,
    approved_specialist: 0,
  },
  {
    id: 'post-incident-review',
    code: 'POST_INCIDENT_REVIEW',
    title: 'Post-Incident Review (PIR)',
    description:
      'Document d\'analyse post-incident qui examine les causes, la réponse, les impacts et les leçons apprises d\'un incident. Définit les processus d\'amélioration continue selon ITIL 4, COBIT 2019 et ISO 27001.',
    frameworks: ['ITIL 4', 'COBIT 2019', 'ISO 27001', 'NIST CSF 2.0'],
    domain: 'Cybersécurité',
    color: 'blue',
    status: 'production',
    approved_dany: 3,
    approved_specialist: 0,
  },
  {
    id: 'after-action-report',
    code: 'AFTER_ACTION_REPORT',
    title: 'After-Action Report (AAR)',
    description:
      'Rapport détaillé d\'action après incident qui documente la chronologie, les actions prises, les résultats et les recommandations. Aligné sur NIST CSF 2.0, ITIL 4 et ISO 27001.',
    frameworks: ['NIST CSF 2.0', 'ITIL 4', 'ISO 27001'],
    domain: 'Cybersécurité',
    color: 'orange',
    status: 'production',
    approved_dany: 3,
    approved_specialist: 0,
  },
  {
    id: 'rapport-lecons-apprises',
    code: 'LESSONS_LEARNED_REPORT',
    title: 'Rapport de leçons apprises',
    description:
      'Document synthétique des leçons apprises suite à un incident, incluant les bonnes pratiques identifiées, les améliorations à apporter et les actions correctives. Aligné sur ITIL 4, COBIT 2019 et ISO 27001.',
    frameworks: ['ITIL 4', 'COBIT 2019', 'ISO 27001'],
    domain: 'Cybersécurité',
    color: 'emerald',
    status: 'production',
    approved_dany: 3,
    approved_specialist: 0,
  },
  {
    id: 'ods',
    code: 'ODS',
    title: 'Offre de Service (ODS)',
    description:
      'Document normé qui décrit l’offre de service BZ pour le client: catalogue, SLA, gouvernance, KPIs et modèle d’engagement.',
    frameworks: ['ITIL', 'COBIT'],
    domain: 'Ventes & Marketing',
    color: 'orange',
    status: 'production',
    approved_dany: 0,
    approved_specialist: 0,
  },
  {
    id: 'plan-cyber',
    code: 'PLAN_CYBER',
    title: 'Plan directeur cybersécurité',
    description:
      'Feuille de route cybersécurité alignée sur NIST 2.0, ISO 27001 et Loi 25, avec priorisation et plan de mise en œuvre.',
    frameworks: ['NIST CSF 2.0', 'ISO 27001', 'Loi 25'],
    domain: 'Cybersécurité',
    color: 'purple',
    status: 'coming_soon',
    approved_dany: 0,
    approved_specialist: 0,
  },
  // Loi 25 – Politique & conformité
  {
    id: 'loi25-politique-confidentialite',
    code: 'LOI25_POLITIQUE_CONF',
    title: 'Politique de confidentialité',
    description:
      'Document normé qui explique comment l’organisation collecte, utilise, conserve et protège les renseignements personnels (Loi 25).',
    frameworks: ['Loi 25', 'Protection des renseignements personnels'],
    domain: 'Cybersécurité',
    color: 'emerald',
    status: 'coming_soon',
    approved_dany: 0,
    approved_specialist: 0,
  },
  {
    id: 'loi25-gestion-renseignements',
    code: 'LOI25_GESTION_RP',
    title: 'Politique de gestion des renseignements personnels',
    description:
      'Cadre interne qui décrit les rôles, responsabilités et processus pour gérer les renseignements personnels conformément à la Loi 25.',
    frameworks: ['Loi 25', 'Gouvernance des données'],
    domain: 'Cybersécurité',
    color: 'emerald',
    status: 'coming_soon',
    approved_dany: 0,
    approved_specialist: 0,
  },
  {
    id: 'loi25-plan-incident-confidentialite',
    code: 'LOI25_PLAN_INCIDENT_CONF',
    title: "Plan d'intervention en cas d'incident de confidentialité",
    description:
      'Procédures détaillées pour gérer un incident impliquant des renseignements personnels: détection, analyse, notification et corrections.',
    frameworks: ['Loi 25', 'Gestion des incidents'],
    domain: 'Cybersécurité',
    color: 'purple',
    status: 'coming_soon',
    approved_dany: 0,
    approved_specialist: 0,
  },
  {
    id: 'loi25-form-consentement-employes',
    code: 'LOI25_FORM_CONSENT_EMP',
    title: 'Formulaire de consentements (employés)',
    description:
      'Gabarit de formulaire pour obtenir, tracer et gérer le consentement des employés concernant l’utilisation de leurs renseignements personnels.',
    frameworks: ['Loi 25'],
    domain: 'Cybersécurité',
    color: 'blue',
    status: 'coming_soon',
    approved_dany: 0,
    approved_specialist: 0,
  },
  {
    id: 'loi25-form-consentement-clients',
    code: 'LOI25_FORM_CONSENT_CLIENTS',
    title: 'Formulaire de consentements (clients)',
    description:
      'Gabarit de formulaire pour documenter le consentement des clients sur la collecte et l’utilisation de leurs données.',
    frameworks: ['Loi 25'],
    domain: 'Cybersécurité',
    color: 'blue',
    status: 'coming_soon',
    approved_dany: 0,
    approved_specialist: 0,
  },
  // Cybersécurité – Politiques & rapports
  {
    id: 'cyber-politique-actifs-ti',
    code: 'CYBER_POLITIQUE_ACTIFS_TI',
    title: "Politique de cybersécurité sur l'utilisation des actifs TI",
    description:
      "Politique qui encadre l'utilisation acceptable des équipements, logiciels, réseaux et données de l'organisation.",
    frameworks: ['NIST CSF 2.0', 'ISO 27001'],
    domain: 'Cybersécurité',
    color: 'purple',
    status: 'coming_soon',
    approved_dany: 0,
    approved_specialist: 0,
  },
  {
    id: 'cyber-politique-utilisation-ia',
    code: 'CYBER_POLITIQUE_IA',
    title: "Politique d'utilisation de l'IA",
    description:
      'Document qui balise l’usage de l’IA générative et analytique: cas permis, cas interdits, sécurité, confidentialité et gouvernance.',
    frameworks: ['NIST AI RMF 1.0', 'ISO/IEC 42001'],
    domain: 'Cybersécurité',
    color: 'purple',
    status: 'coming_soon',
    approved_dany: 0,
    approved_specialist: 0,
  },
  {
    id: 'cyber-rapport-balayage-vulnerabilites',
    code: 'CYBER_RAPPORT_VULN',
    title: 'Rapport de balayage de vulnérabilités',
    description:
      'Rapport détaillant les résultats de scans de vulnérabilités, leur criticité et les recommandations de remédiation.',
    frameworks: ['NIST CSF 2.0', 'OWASP'],
    domain: 'Cybersécurité',
    color: 'indigo',
    status: 'coming_soon',
    approved_dany: 0,
    approved_specialist: 0,
  },
  {
    id: 'cyber-rapport-campagne-phishing',
    code: 'CYBER_RAPPORT_PHISHING',
    title: "Rapport de campagne d'hameçonnage",
    description:
      'Synthèse des résultats d’une campagne de simulation de phishing, avec taux de clic, tendances et plan d’amélioration.',
    frameworks: ['NIST CSF 2.0', 'Sensibilisation sécurité'],
    domain: 'Cybersécurité',
    color: 'indigo',
    status: 'coming_soon',
    approved_dany: 0,
    approved_specialist: 0,
  },
  {
    id: 'cyber-rapport-eval-risques-ti',
    code: 'CYBER_RAPPORT_RISQUES_TI',
    title: "Rapport d'évaluation des risques TI",
    description:
      'Évaluation structurée des risques TI (actifs, menaces, vulnérabilités, impacts, probabilités) avec matrice et plan de traitement.',
    frameworks: ['NIST CSF 2.0', 'ISO 27005'],
    domain: 'Cybersécurité',
    color: 'emerald',
    status: 'coming_soon',
    approved_dany: 0,
    approved_specialist: 0,
  },
  {
    id: 'cyber-rapport-eval-risques-tiers',
    code: 'CYBER_RAPPORT_RISQUES_TIERS',
    title: "Rapport d'évaluation des risques tiers",
    description:
      'Rapport qui couvre les risques liés aux fournisseurs et partenaires (tierces parties) et les mesures de mitigation associées.',
    frameworks: ['NIST CSF 2.0', 'Gestion des fournisseurs'],
    domain: 'Cybersécurité',
    color: 'emerald',
    status: 'coming_soon',
    approved_dany: 0,
    approved_specialist: 0,
  },
  {
    id: 'cyber-rapport-risque-humain',
    code: 'CYBER_RAPPORT_RISQUE_HUMAIN',
    title: "Rapport sur l'évaluation du risque humain",
    description:
      'Analyse du facteur humain en cybersécurité: comportements, formations, erreurs fréquentes et recommandations pour réduire le risque.',
    frameworks: ['NIST CSF 2.0', 'Sensibilisation sécurité'],
    domain: 'Cybersécurité',
    color: 'emerald',
    status: 'coming_soon',
    approved_dany: 0,
    approved_specialist: 0,
  },
  {
    id: 'cyber-plan-incident',
    code: 'CYBER_PLAN_INCIDENT',
    title: "Plan d'intervention en cas d'incident",
    description:
      'Plan opérationnel pour répondre aux incidents de cybersécurité: rôles, étapes, communication, preuves et leçons apprises.',
    frameworks: ['NIST CSF 2.0', 'Gestion des incidents'],
    domain: 'Cybersécurité',
    color: 'purple',
    status: 'coming_soon',
    approved_dany: 0,
    approved_specialist: 0,
  },
  {
    id: 'cyber-plan-reprise',
    code: 'CYBER_PLAN_REPRISE',
    title: 'Plan de reprise',
    description:
      'Plan de reprise des activités TI après un incident majeur: scénarios, priorités, RPO/RTO, procédures techniques et communication.',
    frameworks: ['NIST CSF 2.0', 'Plan de continuité'],
    domain: 'Cybersécurité',
    color: 'purple',
    status: 'coming_soon',
    approved_dany: 0,
    approved_specialist: 0,
  },
  {
    id: 'cyber-politique-gestion-securite-actifs',
    code: 'CYBER_POLITIQUE_GESTION_ACTIFS',
    title: 'Politique de cybersécurité sur la gestion de la sécurité des actifs TI',
    description:
      'Politique qui décrit comment les actifs TI sont inventoriés, classifiés, protégés et suivis tout au long de leur cycle de vie.',
    frameworks: ['NIST CSF 2.0', 'ISO 27001'],
    domain: 'Cybersécurité',
    color: 'purple',
    status: 'coming_soon',
    approved_dany: 0,
    approved_specialist: 0,
  },
];

export const QUALITY_LEVELS: QualityLevelDefinition[] = [
  {
    level: 1,
    label: 'Niveau 1 – Ad hoc',
    shortLabel: 'Ad hoc',
    description:
      'Document de base, surtout pour débloquer la conversation. Processus peu formalisé, contenu suffisant pour initier la réflexion.',
  },
  {
    level: 2,
    label: 'Niveau 2 – Repeatable',
    shortLabel: 'Repeatable',
    description:
      'Document structuré avec les sections clés. Permet de répéter un niveau de qualité minimal et cohérent entre les clients.',
  },
  {
    level: 3,
    label: 'Niveau 3 – Defined',
    shortLabel: 'Defined',
    description:
      'Document complet, méthodologie claire, sections bien définies, aligné formellement sur les bonnes pratiques ITIL / COBIT.',
  },
  {
    level: 4,
    label: 'Niveau 4 – Managed',
    shortLabel: 'Managed',
    description:
      'Document très structuré avec indicateurs, risques, gouvernance et liens forts avec les cadres ITIL, COBIT, NIST.',
  },
  {
    level: 5,
    label: 'Niveau 5 – Optimized',
    shortLabel: 'Optimized',
    description:
      'Document haut de gamme exécutif: vision, benchmarks, tableaux de bord, amélioration continue. Taillé pour un comité de direction.',
  },
];
